import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { lazy, Suspense, useState, type CSSProperties, type ReactElement } from 'react'

import {
  KpiGrid,
  RetentionHeatmap,
  analyticsDashboardQuery,
  analyticsRevenueHistoryQuery,
} from '~/features/analytics'

/**
 * `/admin/analytics` — the Analytics surface.
 *
 * Composition (top → bottom):
 *   1. Header band: title + range chips (90/180/365 days).
 *   2. KPI grid (6-up) — eager, lightweight.
 *   3. Subscription funnel + Plan distribution (Recharts; lazy).
 *   4. MRR/ARR over time (Recharts; lazy).
 *   5. Churn by cohort + Retention heatmap (mixed: Recharts + custom SVG).
 *   6. Conversion + Geo placeholder cards.
 *
 * Lazy-loading pulls Recharts out of the initial route bundle. Each
 * lazy chart sits behind its own `<Suspense>` so a slow chunk on one
 * card never blocks the rest of the page from rendering.
 *
 * Date range is local UI state today — the analytics-service does not
 * yet accept a date-range filter on the dashboard endpoint; only the
 * revenue-history endpoint takes a `months` param. Wiring the chip row
 * to a future `?from=&to=` query is a localized change once that
 * endpoint exists.
 */
export const Route = createFileRoute('/admin/analytics')({
  component: AnalyticsRoute,
})

const SubscriptionFunnelChart = lazy(
  () => import('~/features/analytics/components/SubscriptionFunnelChart'),
)
const PlanDistributionChart = lazy(
  () => import('~/features/analytics/components/PlanDistributionChart'),
)
const MrrArrChart = lazy(
  () => import('~/features/analytics/components/MrrArrChart'),
)
const ChurnChart = lazy(
  () => import('~/features/analytics/components/ChurnChart'),
)

const RANGE_OPTIONS: ReadonlyArray<{
  readonly id: '7d' | '30d' | '90d' | '12m'
  readonly label: string
  readonly months: number
}> = [
  { id: '7d', label: '7d', months: 1 },
  { id: '30d', label: '30d', months: 3 },
  { id: '90d', label: '90d', months: 6 },
  { id: '12m', label: '12m', months: 12 },
]

type RangeId = (typeof RANGE_OPTIONS)[number]['id']

function AnalyticsRoute(): ReactElement {
  const [rangeId, setRangeId] = useState<RangeId>('30d')
  const range = RANGE_OPTIONS.find((r) => r.id === rangeId) ?? RANGE_OPTIONS[1]
  const months = range?.months ?? 12

  const dashboard = useQuery(analyticsDashboardQuery())
  const revenueHistory = useQuery(analyticsRevenueHistoryQuery(months))

  return (
    <div className="ka-page ka-analytics-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Analytics</h1>
          <p className="ka-page-header-subtitle">
            Revenue, subscriptions, churn, retention.
          </p>
        </div>
        <div
          role="radiogroup"
          aria-label="Date range"
          className="ka-filter-group"
        >
          {RANGE_OPTIONS.map((opt) => {
            const active = opt.id === rangeId
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={active}
                className={`ka-detail-chip${active ? ' is-active' : ''}`}
                onClick={() => setRangeId(opt.id)}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      </header>

      <div className="ka-page-section">
        <KpiGrid metrics={dashboard.data} isLoading={dashboard.isLoading} />
      </div>

      <div className="ka-grid ka-grid-2 ka-page-section">
        <Suspense fallback={<ChartFallback height={300} />}>
          <SubscriptionFunnelChart
            metrics={dashboard.data}
            isLoading={dashboard.isLoading}
          />
        </Suspense>
        <Suspense fallback={<ChartFallback height={300} />}>
          <PlanDistributionChart
            data={dashboard.data?.subscriptions.byPlan}
            isLoading={dashboard.isLoading}
          />
        </Suspense>
      </div>

      <div className="ka-page-section">
        <Suspense fallback={<ChartFallback height={340} />}>
          <MrrArrChart
            data={revenueHistory.data}
            isLoading={revenueHistory.isLoading}
            months={months}
          />
        </Suspense>
      </div>

      <div className="ka-grid ka-grid-2 ka-page-section">
        <Suspense fallback={<ChartFallback height={260} />}>
          <ChurnChart
            data={revenueHistory.data}
            isLoading={revenueHistory.isLoading}
          />
        </Suspense>
        <RetentionHeatmap />
      </div>

      <div className="ka-grid ka-grid-2 ka-page-section">
        <PlaceholderCard
          title="Conversion events"
          subtitle="Wire the conversion-events service to populate."
        />
        <PlaceholderCard
          title="Geo distribution"
          subtitle="Backed by user_profiles.country once the column ships."
        />
      </div>

      {(dashboard.isError || revenueHistory.isError) && (
        <div role="alert" className="ka-page-strip is-warning ka-page-section">
          Couldn&apos;t load some analytics data. Charts above show what we
          have; refresh to retry.
        </div>
      )}
    </div>
  )
}

interface ChartFallbackProps {
  readonly height: number
}

function ChartFallback({ height }: ChartFallbackProps): ReactElement {
  return (
    <section className="ka-card ka-card--flush" aria-busy="true">
      <div className="ka-chart-fallback-wrap">
        <div
          className="ka-sk ka-sk--chart-dynamic"
          style={{ '--ka-chart-h': `${height}px` } as CSSProperties}
        />
      </div>
    </section>
  )
}

interface PlaceholderCardProps {
  readonly title: string
  readonly subtitle: string
}

function PlaceholderCard({
  title,
  subtitle,
}: PlaceholderCardProps): ReactElement {
  return (
    <section className="ka-card ka-card--flush" aria-label={title}>
      <header className="ka-card-head">
        <div className="ka-card-head-title">{title}</div>
      </header>
      <div className="ka-table-empty">
        {subtitle}
      </div>
    </section>
  )
}
